import { useEffect, useRef, useState } from "react";
import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { FilterValues } from "../../../../data-model/filterValues";
import { TableNames } from "../../../../enums/tableNames";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { createFilterPayloadForAllOpenOrdersForPostgres } from "../utils/generateFilterPayloadForAllOpenOrders";

/**
 * Builds and maintains the Postgres search payload for All Open Orders based on
 * active filters, search term, org ID, and the persisted sort state from
 * `tablesContext` (`sortByField` / `sortAscending`). Defers payload generation
 * until after the initial data fetch completes to avoid duplicate requests.
 *
 * @param validFilters - Currently applied filter values
 * @param searchTerm - Free-text search string
 * @param initialDataFetchingExecuted - Whether the initial table data load has finished
 * @returns The current filterPayload and its setter
 */
export const useFilterPayload = (
  validFilters: FilterValues[],
  searchTerm: string,
  initialDataFetchingExecuted: boolean,
) => {
  const orgId = getOrgIdForFetchExternalWorklist();
  const { getTableDataFromContext } = useTablesContext();
  const tableAttributes = getTableDataFromContext(TableNames.ALL_OPEN_ORDERS);
  const sortByField = tableAttributes?.sortByField;
  const sortAscending = tableAttributes?.sortAscending;

  const [filterPayload, setFilterPayload] =
    useState<SearchForMedicalPaOrdersFromPostgresParams | null>(null);

  const hasInitialFetchCompleted = useRef(false);

  useEffect(() => {
    if (initialDataFetchingExecuted && !hasInitialFetchCompleted.current) {
      hasInitialFetchCompleted.current = true;
    }

    if (!hasInitialFetchCompleted.current) return;

    const payload = createFilterPayloadForAllOpenOrdersForPostgres(
      validFilters,
      searchTerm,
      orgId,
      sortByField,
      sortAscending,
    );
    setFilterPayload(payload);
  }, [
    initialDataFetchingExecuted,
    validFilters,
    searchTerm,
    orgId,
    sortByField,
    sortAscending,
  ]);

  return { filterPayload, setFilterPayload };
};
